import { createTheme, responsiveFontSizes, alpha, PaletteMode } from '@mui/material/styles';

// Define color palette
const primaryColor = '#4E67EB'; // Main blue for brand
const secondaryColor = '#FF4E71'; // Coral accent
const infoColor = '#00B3E6'; // Light blue for information
const successColor = '#10B981'; // Modern emerald green for success states
const warningColor = '#FFB648'; // Orange for warnings
const errorColor = '#FF4B4B'; // Red for errors

// Create theme based on light/dark mode
export const createAppTheme = (mode: PaletteMode) => {
  // Colors that change based on theme mode
  const backgroundDefault = mode === 'light' ? '#F9FAFC' : '#1F2129';
  const backgroundPaper = mode === 'light' ? '#FFFFFF' : '#1F2129';
  const textPrimary = mode === 'light' ? '#1A1C21' : '#FFFFFF';
  const textSecondary = mode === 'light' ? '#666A75' : 'rgba(255, 255, 255, 0.72)';
  const dividerColor = mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.12)';

  const baseTheme = createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
        light: mode === 'light' ? '#7A8EFF' : '#6272D6',
        dark: mode === 'light' ? '#2E42B6' : '#3B4CB1',
        contrastText: '#FFFFFF',
      },
      secondary: {
        main: secondaryColor,
        light: mode === 'light' ? '#FF7E96' : '#E55A76',
        dark: mode === 'light' ? '#CC3A59' : '#BD364F',
        contrastText: '#FFFFFF',
      },
      info: {
        main: infoColor,
      },
      success: {
        main: successColor,
      },
      warning: {
        main: warningColor,
      },
      error: {
        main: errorColor,
      },
      background: {
        default: backgroundDefault,
        paper: backgroundPaper,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
      },
      divider: dividerColor,
    },
    typography: {
      fontFamily: '"Figtree", "Helvetica", "Arial", sans-serif',
      h1: {
        fontWeight: 700,
        fontSize: '2.5rem',
      },
      h2: {
        fontWeight: 700,
        fontSize: '2rem',
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 600,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 600,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 600,
        fontSize: '1rem',
      },
      body1: {
        fontSize: '1rem',
      },
      body2: {
        fontSize: '0.875rem',
      },
      button: {
        textTransform: 'none',
        fontWeight: 600,
      },
    },
    shape: {
      borderRadius: 12,
    },
    shadows: [
      'none',
      '0px 2px 4px rgba(0, 0, 0, 0.05)',
      '0px 4px 8px rgba(0, 0, 0, 0.05)',
      '0px 8px 16px rgba(0, 0, 0, 0.05)',
      '0px 16px 24px rgba(0, 0, 0, 0.05)',
      '0px 20px 40px rgba(0, 0, 0, 0.05)',
      '0px 24px 48px rgba(0, 0, 0, 0.05)',
      '0px 28px 56px rgba(0, 0, 0, 0.05)',
      '0px 32px 64px rgba(0, 0, 0, 0.05)',
      '0px 36px 72px rgba(0, 0, 0, 0.05)',
      '0px 40px 80px rgba(0, 0, 0, 0.05)',
      '0px 44px 88px rgba(0, 0, 0, 0.05)',
      '0px 48px 96px rgba(0, 0, 0, 0.05)',
      '0px 52px 104px rgba(0, 0, 0, 0.05)',
      '0px 56px 112px rgba(0, 0, 0, 0.05)',
      '0px 60px 120px rgba(0, 0, 0, 0.05)',
      '0px 64px 128px rgba(0, 0, 0, 0.05)',
      '0px 68px 136px rgba(0, 0, 0, 0.05)',
      '0px 72px 144px rgba(0, 0, 0, 0.05)',
      '0px 76px 152px rgba(0, 0, 0, 0.05)',
      '0px 80px 160px rgba(0, 0, 0, 0.05)',
      '0px 84px 168px rgba(0, 0, 0, 0.05)',
      '0px 88px 176px rgba(0, 0, 0, 0.05)',
      '0px 92px 184px rgba(0, 0, 0, 0.05)',
      '0px 96px 192px rgba(0, 0, 0, 0.05)',
    ],
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            padding: '10px 16px',
            boxShadow: 'none',
            textTransform: 'uppercase',
            minHeight: '40px',
            letterSpacing: '0.1em',
            '&:hover': {
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            },
          },
          outlined: {
            borderRadius: '24px',
            borderColor: '#5D54FF',
            borderWidth: '1.25px',
          },
          containedPrimary: {
            background: mode === 'light' 
              ? `linear-gradient(45deg, ${primaryColor} 30%, #5D77FF 90%)`
              : `linear-gradient(45deg, ${primaryColor} 30%, #465AD9 90%)`,
          },
          containedSecondary: {
            background: mode === 'light'
              ? `linear-gradient(45deg, ${secondaryColor} 30%, #FF638A 90%)`
              : `linear-gradient(45deg, ${secondaryColor} 30%, #E64A6A 90%)`,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: mode === 'light' 
              ? '0px 4px 20px rgba(0, 0, 0, 0.05)'
              : '0px 4px 20px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
          },
          elevation1: {
            boxShadow: mode === 'light'
              ? '0px 2px 8px rgba(0, 0, 0, 0.05)'
              : '0px 2px 8px rgba(0, 0, 0, 0.15)',
          },
          elevation2: {
            boxShadow: mode === 'light' 
              ? '0px 4px 12px rgba(0, 0, 0, 0.05)'
              : '0px 4px 12px rgba(0, 0, 0, 0.2)',
          },
          elevation3: {
            boxShadow: mode === 'light'
              ? '0px 8px 16px rgba(0, 0, 0, 0.05)'
              : '0px 8px 16px rgba(0, 0, 0, 0.25)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 8,
            },
          },
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: backgroundDefault,
            transition: 'background-color 0.2s ease',
            height: '100%',
            overflow: 'auto',
            margin: 0,
            padding: 0,
          },
          html: {
            height: '100%',
            overflow: 'auto',
          },
          '#root': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          // Fix scrolling issues
          '.MuiBox-root': {
            '&[style*="minHeight: 100vh"]': {
              minHeight: '100vh !important',
              height: 'auto !important',
            },
          },
          // Ensure proper overflow handling
          '.MuiDrawer-root, .MuiDrawer-paper': {
            overflow: 'auto !important',
          },
          // Fix any potential double scrolling
          'body > *': {
            overflow: 'visible !important',
          },
          // Ensure proper viewport handling
          'html, body': {
            scrollBehavior: 'smooth',
          },

        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 42,
            height: 26,
            padding: 0,
          },
          switchBase: {
            padding: 1,
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
            }
          },
          thumb: {
            width: 24,
            height: 24,
          },
          track: {
            borderRadius: 13,
            border: 'none',
          },
        },
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });

  return responsiveFontSizes(baseTheme);
};

// Default theme — landing is dark-only
const theme = createAppTheme('dark');

export default theme; 